import { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, X, FileText, Plus } from "lucide-react";
import { reportsService, Report } from "@/services/reportsService";
import { toast } from "sonner";

interface ReportFormData {
  report_type: "new" | "season";
  name: string;
  category: "Original" | "Consolidation" | "Affiliate";
  season: number;
  previous_report_id?: string;
  sample_size?: number;
  questionnaire_length_minutes?: number;
  source?: string;
  external_costs?: {
    panel?: number;
    analyst?: number;
    data?: number;
  };
  start_date?: string;
  delivery_date?: string;
}

interface ReportFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingReport?: Report | null;
}

const categoryColors = {
  Original: "bg-gray-900 text-white",
  Consolidation: "bg-red-500 text-white", 
  Affiliate: "bg-blue-500 text-white"
};

export function ReportFormModal({ open, onOpenChange, editingReport }: ReportFormModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const queryClient = useQueryClient();
  
  const form = useForm<ReportFormData>({
    defaultValues: {
      report_type: "new",
      season: 1,
      external_costs: { panel: 0, analyst: 0, data: 0 }
    }
  });
  const watchedCategory = form.watch("category");
  const watchedReportType = form.watch("report_type");

  const { data: previousReports = [] } = useQuery({
    queryKey: ['reports', 'category', watchedCategory],
    queryFn: () => watchedCategory ? reportsService.getReportsByCategory(watchedCategory) : Promise.resolve([]),
    enabled: !!watchedCategory && watchedReportType === "season"
  });

  const { data: reportFiles = [] } = useQuery({
    queryKey: ['report-files', editingReport?.id],
    queryFn: () => editingReport?.id ? reportsService.getReportFiles(editingReport.id) : Promise.resolve([]),
    enabled: !!editingReport?.id
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => reportsService.createReport(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report created successfully');
      if (selectedFiles.length === 0) {
        onOpenChange(false);
        form.reset();
      }
      return result;
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => reportsService.updateReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report updated successfully');
      onOpenChange(false);
    }
  });

  const uploadFilesMutation = useMutation({
    mutationFn: async ({ reportId, files }: { reportId: string; files: File[] }) => {
      const uploadPromises = files.map(file => reportsService.uploadReportFile(reportId, file));
      return Promise.all(uploadPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-files'] });
      queryClient.invalidateQueries({ queryKey: ['report-files', editingReport?.id] });
      toast.success('Files uploaded successfully');
      setSelectedFiles([]);
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: ({ fileId, filePath }: { fileId: string; filePath: string }) => 
      reportsService.deleteReportFile(fileId, filePath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-files'] });
      queryClient.invalidateQueries({ queryKey: ['report-files', editingReport?.id] });
      toast.success('File deleted successfully');
    }
  });

  const onSubmit: SubmitHandler<ReportFormData> = async (data) => {
    try {
      const { report_type, ...reportData } = data;
      const formData = {
        ...reportData,
        external_costs: reportData.external_costs || { panel: 0, analyst: 0, data: 0 }
      };
      
      if (editingReport) {
        await updateMutation.mutateAsync({ id: editingReport.id, data: formData });
        // Upload new files if any selected during edit
        if (selectedFiles.length > 0) {
          await uploadFilesMutation.mutateAsync({ reportId: editingReport.id, files: selectedFiles });
        }
      } else {
        const result = await createMutation.mutateAsync(formData);
        if (selectedFiles.length > 0 && result?.id) {
          await uploadFilesMutation.mutateAsync({ reportId: result.id, files: selectedFiles });
        }
        onOpenChange(false);
        form.reset();
        setSelectedFiles([]);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Failed to save report');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (editingReport) {
      const externalCosts = editingReport.external_costs as any;
      form.reset({
        report_type: "new", // Default for editing
        name: editingReport.name,
        category: editingReport.category as "Original" | "Consolidation" | "Affiliate",
        season: editingReport.season,
        sample_size: editingReport.sample_size || undefined,
        questionnaire_length_minutes: editingReport.questionnaire_length_minutes || undefined,
        source: editingReport.source || '',
        external_costs: {
          panel: externalCosts?.panel || 0,
          analyst: externalCosts?.analyst || 0,
          data: externalCosts?.data || 0
        },
        start_date: editingReport.start_date || '',
        delivery_date: editingReport.delivery_date || ''
      });
    } else {
      form.reset({
        report_type: "new",
        season: 1,
        external_costs: { panel: 0, analyst: 0, data: 0 }
      });
    }
    setSelectedFiles([]);
  }, [editingReport, form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingReport ? 'Edit Report' : 'Create New Report'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* First Row: Category and Report Type */}        
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select 
                value={form.watch("category")} 
                onValueChange={(value) => form.setValue("category", value as "Original" | "Consolidation" | "Affiliate")}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Original">Original</SelectItem>
                  <SelectItem value="Consolidation">Consolidation</SelectItem>
                  <SelectItem value="Affiliate">Affiliate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {!editingReport && (
              <div>
                <Label>Report Type</Label>
                <Select 
                  value={form.watch("report_type")} 
                  onValueChange={(value) => form.setValue("report_type", value as "new" | "season")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Report</SelectItem>
                    <SelectItem value="season">New Season of Existing Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Second Row: Report Name and Season (or Previous Report and Season) */}
          <div className="grid grid-cols-2 gap-4">
            {watchedReportType === "season" && !editingReport ? (
              <div>
                <Label>Previous Report *</Label>
                <Select 
                  value={form.watch("previous_report_id") || ""} 
                  onValueChange={(value) => form.setValue("previous_report_id", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select previous report" />
                  </SelectTrigger>
                  <SelectContent>
                    {previousReports.map((report) => (
                      <SelectItem key={report.id} value={report.id}>
                        {report.name} (Season {report.season})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Report Name *</Label>
                <Input {...form.register("name", { required: watchedReportType === "new" })} />
              </div>
            )}

            <div>
              <Label>Season *</Label>
              <Input type="number" {...form.register("season", { valueAsNumber: true })} min="1" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input type="date" {...form.register("start_date")} />
            </div>
            <div>
              <Label>Upload Date</Label>
              <Input type="date" {...form.register("delivery_date")} />
            </div>
          </div>

          {/* Category-specific fields */}
          {watchedCategory === "Original" && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Original Report Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sample Size</Label>
                  <Input type="number" {...form.register("sample_size", { valueAsNumber: true })} min="0" />
                </div>
                <div>
                  <Label>Questionnaire Length (minutes)</Label>
                  <Input type="number" {...form.register("questionnaire_length_minutes", { valueAsNumber: true })} min="0" />
                </div>
              </div>
            </div>
          )}

          {(watchedCategory === "Consolidation" || watchedCategory === "Affiliate") && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{watchedCategory} Report Details</h3>
              <div>
                <Label>Source</Label>
                <Textarea {...form.register("source")} placeholder="Enter source information..." />
              </div>
            </div>
          )}

          {/* External Costs */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">External Costs (€)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Panel</Label>
                <Input type="number" {...form.register("external_costs.panel", { valueAsNumber: true })} min="0" step="0.01" />
              </div>
              <div>
                <Label>Analyst</Label>
                <Input type="number" {...form.register("external_costs.analyst", { valueAsNumber: true })} min="0" step="0.01" />
              </div>
              <div>
                <Label>Data</Label>
                <Input type="number" {...form.register("external_costs.data", { valueAsNumber: true })} min="0" step="0.01" />
              </div>
            </div>
          </div>

          {/* Files Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Files</h3>
            
            {/* Existing Files (for edit mode) */}
            {editingReport && reportFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Existing Files</Label>
                {reportFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-2 bg-muted rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">{file.name}</span>
                      {file.file_size && (
                        <span className="text-xs text-muted-foreground">
                          ({(file.file_size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteFileMutation.mutate({ fileId: file.id, filePath: file.file_path })}
                      disabled={deleteFileMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-4">
              <Label>{editingReport ? 'Upload Additional Files' : 'Upload Files'}</Label>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-primary hover:underline">Upload files</span>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        className="sr-only"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      />
                    </label>
                    <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                  </div>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>New Files to Upload</Label>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {editingReport && selectedFiles.length > 0 && (
                <Button
                  type="button"
                  onClick={() => uploadFilesMutation.mutate({ reportId: editingReport.id, files: selectedFiles })}
                  disabled={uploadFilesMutation.isPending}
                  className="w-full"
                >
                  {uploadFilesMutation.isPending ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingReport ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}